import { type FunctionComponent, type ReactNode, Suspense, useEffect } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, usePreloadedQuery, useQueryLoader } from "react-relay";

import type { controlQueryLoaderAbWidgetQuery } from "scmp-app/queries/__generated__/controlQueryLoaderAbWidgetQuery.graphql";

import type { ControlQueryVariables } from "./index";

const controlQueryLoaderAbWidgetQueryNode = graphql`
  query controlQueryLoaderAbWidgetQuery(
    $chinaFutureTechSectionEntityId: String!
    $chinaFutureTechStoryPackageArticlesQueueName: String!
    $secondaryStoryPackageArticlesQueueName: String!
    $seriesWidgetArticlesQueueName: String!
    $spotlightQueueName: String!
  ) {
    ...seriesWidgetHomeV2Query
      @arguments(seriesWidgetArticlesQueueName: $seriesWidgetArticlesQueueName)
    ...secondaryStoryPackageHomeV2Query
      @arguments(secondaryStoryPackageArticlesQueueName: $secondaryStoryPackageArticlesQueueName)
    ...chinaFutureTechStoryPackageHomeV2Query
      @arguments(
        chinaFutureTechSectionEntityId: $chinaFutureTechSectionEntityId
        chinaFutureTechStoryPackageArticlesQueueName: $chinaFutureTechStoryPackageArticlesQueueName
      )
    ...spotlightHomeV2Query @arguments(spotlightQueueName: $spotlightQueueName)
    seriesWidgetEntityIds: queue(filter: { name: $seriesWidgetArticlesQueueName }) {
      items {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
    openQuestionWidgetEntityIds: queue(filter: { name: $secondaryStoryPackageArticlesQueueName }) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
    cftWidgetEntityIds: queue(filter: { name: $chinaFutureTechStoryPackageArticlesQueueName }) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
    spotlightWidgetEntityIds: queue(filter: { name: $spotlightQueueName }) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
  }
`;

type Props = {
  children: (queryReference: QueryResponse) => ReactNode;
  onEntityIds: (entityIds: string[]) => void;
  renderPlaceholders: () => ReactNode;
  variables: ControlQueryVariables;
};

type QueryResponse = controlQueryLoaderAbWidgetQuery["response"];

export const AbWidgetControlLoader: FunctionComponent<Props> = ({
  children,
  onEntityIds,
  renderPlaceholders,
  variables,
}) => {
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<controlQueryLoaderAbWidgetQuery>(
    controlQueryLoaderAbWidgetQueryNode,
  );

  useEffect(() => {
    loadQuery(variables, { fetchPolicy: "network-only" });
    return () => disposeQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!queryReference) {
    return <>{renderPlaceholders()}</>;
  }

  return (
    <Suspense fallback={<>{renderPlaceholders()}</>}>
      <AbWidgetControlContent
        node={controlQueryLoaderAbWidgetQueryNode}
        onEntityIds={onEntityIds}
        queryReference={queryReference}
      >
        {children}
      </AbWidgetControlContent>
    </Suspense>
  );
};

const AbWidgetControlContent: FunctionComponent<{
  children: (queryReference: QueryResponse) => ReactNode;
  node: typeof controlQueryLoaderAbWidgetQueryNode;
  onEntityIds: (entityIds: string[]) => void;
  queryReference: PreloadedQuery<controlQueryLoaderAbWidgetQuery>;
}> = ({ children, node, onEntityIds, queryReference }) => {
  const data = usePreloadedQuery(node, queryReference);

  useEffect(() => {
    const entityIds = [
      ...(data.seriesWidgetEntityIds?.items?.edges ?? []),
      ...(data.openQuestionWidgetEntityIds?.items?.edges ?? []),
      ...(data.cftWidgetEntityIds?.items?.edges ?? []),
      ...(data.spotlightWidgetEntityIds?.items?.edges ?? []),
    ]
      .map(edge => edge?.node?.entityId)
      .filter((id): id is string => !!id);
    onEntityIds(entityIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return children(data);
};
